/**
 * SSE stream consumer for the ai-cto /chat endpoint.
 *
 * Reads a fetch Response body as Server-Sent Events.
 * Yields `{ type: 'text', text: string }` chunks and a final `{ type: 'done' }`.
 * On error events from the server, yields `{ type: 'error', message: string }`.
 */

export type StreamChunk =
  | { type: 'text'; text: string }
  | { type: 'done' }
  | { type: 'error'; message: string };

/**
 * Consume a ReadableStream of SSE data and yield parsed chunks.
 * Each SSE line has the form `data: <json>\n\n`.
 */
export async function* readChatStream(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<StreamChunk> {
  const reader = body.getReader();
  const dec = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += dec.decode(value, { stream: true });

      // Process complete SSE messages separated by double newline
      const parts = buffer.split('\n\n');
      // Keep the last (possibly incomplete) part in the buffer
      buffer = parts.pop() ?? '';

      for (const part of parts) {
        for (const line of part.split('\n')) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;
          const raw = trimmed.slice(5).trim();
          if (raw === '[DONE]') {
            yield { type: 'done' };
            return;
          }
          try {
            const parsed = JSON.parse(raw) as StreamChunk;
            yield parsed;
            if (parsed.type === 'done') return;
          } catch {
            // Malformed SSE line — skip
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  yield { type: 'done' };
}
